import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Mail, Send, Loader2, CheckCircle2, XCircle, FlaskConical, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const owesBalance = (a) => {
  if (a.ticket_status === 'cancelled' || a.ticket_status === 'waitlist') return false;
  if (a.amount_paid !== null && a.amount_paid !== undefined) return 325 - a.amount_paid > 0;
  return a.ticket_status === 'pending' || a.ticket_status === 'deposit_paid';
};

const EmailPage = () => {
  const [attendees, setAttendees] = useState([]);
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState('active');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [aRes, hRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/attendees`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/emails/history`, { headers: getAuthHeaders() }),
      ]);
      setAttendees(aRes.data);
      setHistory(hRes.data);
    } catch {
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = useMemo(() => {
    const withEmail = attendees.filter(a => a.email);
    return {
      active: withEmail.filter(a => a.ticket_status !== 'cancelled' && a.ticket_status !== 'waitlist').length,
      paid_full: withEmail.filter(a => a.ticket_status === 'paid_full').length,
      deposit_paid: withEmail.filter(a => a.ticket_status === 'deposit_paid').length,
      pending: withEmail.filter(a => a.ticket_status === 'pending').length,
      waitlist: withEmail.filter(a => a.ticket_status === 'waitlist').length,
      balance_due: withEmail.filter(owesBalance).length,
      all: withEmail.length,
      individual: Object.values(selected).filter(Boolean).length,
    };
  }, [attendees, selected]);

  const AUDIENCES = [
    { value: 'active', label: 'All registered attendees' },
    { value: 'paid_full', label: 'Paid in full' },
    { value: 'deposit_paid', label: 'Deposit paid' },
    { value: 'pending', label: 'Pending payment' },
    { value: 'balance_due', label: 'Balance due' },
    { value: 'waitlist', label: 'Waitlist' },
    { value: 'all', label: 'Everyone (incl. waitlist & cancelled)' },
    { value: 'individual', label: 'Specific people…' },
  ];

  const recipientCount = counts[audience] ?? 0;

  const filteredAttendees = useMemo(() => {
    const q = search.toLowerCase();
    return attendees
      .filter(a => a.email)
      .filter(a => !q || `${a.first_name} ${a.last_name} ${a.email}`.toLowerCase().includes(q))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [attendees, search]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/emails/test`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message, { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are both required'); return; }
    if (recipientCount === 0) { toast.error('No recipients in the selected group'); return; }
    if (!window.confirm(`Send "${subject}" to ${recipientCount} recipient(s) from info@rojoretreat.com?`)) return;

    let payload;
    if (audience === 'individual') {
      const emails = attendees.filter(a => selected[a.id]).map(a => a.email);
      payload = { recipient_type: 'individual', recipient_filter: emails.join(','), subject, body };
    } else if (['paid_full', 'deposit_paid', 'pending', 'waitlist'].includes(audience)) {
      payload = { recipient_type: 'status', recipient_filter: audience, subject, body };
    } else {
      payload = { recipient_type: audience, recipient_filter: null, subject, body };
    }

    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/emails/send`, payload, {
        headers: getAuthHeaders(), timeout: 300000,
      });
      const d = res.data;
      if (d.failed_count > 0) {
        toast.warning(`Sent ${d.sent_count}/${d.total_recipients} — ${d.failed_count} failed`, { duration: 8000 });
      } else {
        toast.success(`Sent to all ${d.sent_count} recipients`, { duration: 6000 });
      }
      setSubject(''); setBody(''); setSelected({});
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="email-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Mail className="h-7 w-7 text-wine" /> Email
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Sends directly from <span className="font-semibold text-gray-700">info@rojoretreat.com</span> — every email lands in your Gmail Sent folder.
          </p>
        </div>
        <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="send-test-email-btn">
          {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
          Send Test to Myself
        </Button>
      </div>

      <Card className="mb-8 border-t-4 border-t-wine">
        <CardContent className="p-5 md:p-6 space-y-5">
          <div className="flex flex-col md:flex-row gap-4 md:items-end">
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">To</label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wine"
                data-testid="audience-select">
                {AUDIENCES.map(a => (
                  <option key={a.value} value={a.value}>
                    {a.label}{a.value !== 'individual' ? ` (${counts[a.value] ?? 0})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-wine-50 border border-wine/20 rounded-lg" data-testid="recipient-count">
              <Users className="h-4 w-4 text-wine" />
              <span className="text-sm font-semibold text-wine-700">{recipientCount} recipient{recipientCount === 1 ? '' : 's'}</span>
            </div>
          </div>

          {audience === 'individual' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)}
                  className="bg-white" data-testid="attendee-search-input" />
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                {filteredAttendees.map(a => (
                  <label key={a.id} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={!!selected[a.id]}
                      onChange={() => setSelected(s => ({ ...s, [a.id]: !s[a.id] }))}
                      className="h-4 w-4 accent-[#7c1d33]" />
                    <span className="text-sm text-gray-800">{a.first_name} {a.last_name}</span>
                    <span className="text-xs text-gray-400 ml-auto">{a.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Final details for the retreat" className="mt-1" data-testid="email-subject-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Message</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
              placeholder="Write your message… plain text is fine, it gets wrapped in the branded ROJO template automatically."
              className="mt-1" data-testid="email-body-input" />
          </div>

          <div className="flex justify-end">
            <Button onClick={send} disabled={sending} data-testid="send-email-btn"
              className="bg-gradient-to-r from-wine to-burgundy text-white hover:opacity-90 px-8">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {sending ? 'Sending…' : `Send to ${recipientCount}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-gray-700 uppercase tracking-wide">Send History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <p className="text-sm text-gray-400 px-6 pb-6">No emails sent yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="px-5 py-2 font-semibold">Subject</th>
                  <th className="px-5 py-2 font-semibold">To</th>
                  <th className="px-5 py-2 font-semibold">Result</th>
                  <th className="px-5 py-2 font-semibold whitespace-nowrap">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`history-row-${h.id}`}>
                    <td className="px-5 py-3 max-w-[260px] truncate text-gray-900 font-medium" title={h.subject}>{h.subject}</td>
                    <td className="px-5 py-3 text-gray-600">{h.recipient_label}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {h.failed_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">
                          <XCircle className="h-3.5 w-3.5" /> {h.sent_count}/{h.recipient_count} sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {h.sent_count} sent
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(h.sent_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailPage;
